import PlusIcon from '@/assets/icons/plus.svg?react';
import SearchIcon from '@/assets/icons/search.svg?react';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import StyledButton from '@/components/styled/StyledButton';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledInput from '@/components/styled/StyledInput';
import StyledListHeader from '@/components/styled/StyledListHeader';
import StyledSelect from '@/components/styled/StyledSelect';
import StyledTable from '@/components/styled/StyledTable';
import ModalDeletion from '@/components/ui/ModalDeletion';
import ModalDuplication from '@/components/ui/ModalDuplication';
import ModalSelection from '@/components/ui/ModalSelection';
import { BENEFIT_TO_PATH } from '@/constants/Paths';
import {
  useDeleteBenefit,
  useDeleteMultipleBenefits,
  useDuplicateBenefit,
  useGetBenefits,
  useGetBenefitsCities,
  useGetCountriesWithBenefits,
  useGetSuppliersWithBenefits,
  useGetTypesWithBenefits,
} from '@/hooks/useBenefits';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import useTableControls from '@/hooks/useTableControls';
import { BENEFIT_COLUMNS } from '@/types/benefits/benefitColumnDef';
import { isBenefitUsedElsewhere } from '@/utils/benefits/isBenefitUsedElsewhere';
import { BENEFIT_TYPES, BenefitType } from '@/types/benefits/benefitTypes';
import { getBenefitDuplicationFields } from '@/utils/benefits/getBenefitFields';
import {
  getBenefitTypeName,
  getBenefitTypeTranslatedLabel,
} from '@/utils/benefits/getBenefitTypeName';
import { BenefitsListUrlState } from '@/utils/searchParams/buildBenefitsListSearch';
import { benefitsListParsers } from '@/utils/searchParams/parsers';
import { RowSelectionState } from '@tanstack/react-table';
import { useQueryStates } from 'nuqs';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { COUNTRIES } from '../../../../../shared/CountriesData';
import StyledNextPreviousButtons from '@/components/styled/StyledNextPreviousButtons';
import { type LabelValue } from '@/types/global';
import { PAGE_SIZE } from '@/constants/pageSize';
import ListCircuitsUsingBenefitModal from '@/components/benefits/ListCircuitsUsingBenefitModal';
import BenefitDeletionAlertModal from '@/components/benefits/BenefitDeletionAlertModal';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { useToast } from '@/hooks/useToast';

type BenefitsListFormValues = {
  search: string;
  countryName: LabelValue<string> | undefined;
  cityName: LabelValue<string> | undefined;
  supplierName: LabelValue<string> | undefined;
  benefitType: LabelValue<string> | undefined;
};

function formValuesToUrl(
  values: BenefitsListFormValues,
  tableState: Pick<BenefitsListUrlState, 'page' | 'sortField' | 'sortOrder'>,
): BenefitsListUrlState {
  const v = values.benefitType?.value ?? null;
  const isActivity = !!v && ['MEAL', 'TRANSFER', 'ACTIVITY'].includes(v);
  return {
    name: values.search,
    supplierid: values.supplierName?.value ?? null,
    country: values.countryName?.value ?? null,
    city: values.cityName?.value ?? null,
    type: isActivity ? 'ACTIVITY' : v,
    activityType: isActivity ? v : null,
    page: tableState.page,
    sortField: tableState.sortField,
    sortOrder: tableState.sortOrder,
  };
}

const ListBenefitsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toastAsync } = useToast();
  const getTranslatedConst = useTranslatedConst();
  const [urlFilters, setUrlFilters] = useQueryStates(benefitsListParsers);

  const { data: suppliersOptions } = useGetSuppliersWithBenefits((data) =>
    data?.map(({ id, name }) => ({ value: id, label: name })),
  );

  const { data: benefitTypeOptions } = useGetTypesWithBenefits((data) =>
    data
      .map((type) => ({
        value: type,
        label: getBenefitTypeTranslatedLabel(t, getBenefitTypeName(type)),
      }))
      .sort((type1, type2) => (type1.label < type2.label ? -1 : 1)),
  );

  const { data: countriesOptions } = useGetCountriesWithBenefits((data) =>
    data?.map(({ country }) => ({ value: country, label: country })),
  );
  const { data: citiesOptions } = useGetBenefitsCities({
    country: urlFilters.country ?? undefined,
    select: (data) => data.map(({ city }) => ({ value: city, label: city })),
  });

  const formValues: BenefitsListFormValues = {
    search: urlFilters.name,
    countryName: urlFilters.country
      ? { value: urlFilters.country, label: urlFilters.country }
      : undefined,
    cityName: urlFilters.city
      ? { value: urlFilters.city, label: urlFilters.city }
      : undefined,
    supplierName: urlFilters.supplierid
      ? suppliersOptions?.find(({ value }) => value === urlFilters.supplierid)
      : undefined,
    benefitType:
      urlFilters.type === 'ACTIVITY'
        ? benefitTypeOptions?.find(
            ({ value }) => value === urlFilters.activityType,
          )
        : urlFilters.type
          ? benefitTypeOptions?.find(({ value }) => value === urlFilters.type)
          : undefined,
  };

  const { control, subscribe } = useForm<BenefitsListFormValues>({
    values: formValues,
  });
  const previousCountryRef = useRef<string | null>(urlFilters.country);

  useEffect(() => {
    const unsub = subscribe({
      formState: { values: true },
      callback: ({ values }) =>
        setUrlFilters(
          formValuesToUrl(values as BenefitsListFormValues, {
            page: 1,
            sortField: urlFilters.sortField,
            sortOrder: urlFilters.sortOrder,
          }),
        ),
    });
    return () => unsub();
  }, [subscribe, setUrlFilters, urlFilters.sortField, urlFilters.sortOrder]);

  useEffect(() => {
    if (previousCountryRef.current !== urlFilters.country && urlFilters.city) {
      setUrlFilters({ city: null });
    }
    previousCountryRef.current = urlFilters.country;
  }, [urlFilters.country, urlFilters.city, setUrlFilters]);

  const initialPage = urlFilters.page > 0 ? urlFilters.page : 1;
  const initialSorting = urlFilters.sortField
    ? [{ id: urlFilters.sortField, desc: urlFilters.sortOrder === 'desc' }]
    : [];
  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls({
      initialPage,
      initialSorting,
      onPageChange: (nextPage) => {
        setUrlFilters({ page: nextPage > 0 ? nextPage : 1 });
      },
      onSortingChange: (nextSorting) => {
        const nextSort = nextSorting[0];
        setUrlFilters({
          page: 1,
          sortField: nextSort?.id ?? '',
          sortOrder: nextSort?.desc ? 'desc' : 'asc',
        });
      },
    });
  const [isBenefitTypeModalOpen, setIsBenefitTypeModalOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [benefitsToDeleteIds, setBenefitsToDeleteIds] = useState<string[]>([]);
  const [benefitToDuplicateId, setBenefitToDuplicateId] = useState<string>();
  const [benefitToDuplicateFields, setBenefitToDuplicateFields] =
    useState<LabelValue<string>[]>();
  const [benefitToDisplayCircuitsId, setBenefitToDisplayCircuitsId] = useState<
    string | null
  >(null);

  const selectedBenefits = Object.keys(rowSelection);

  const debouncedName = useDebouncedSearch(urlFilters.name);

  const apiFilters: Record<string, string> = {
    name: debouncedName,
    ...(urlFilters.supplierid && { supplierid: urlFilters.supplierid }),
    ...(urlFilters.country && { country: urlFilters.country }),
    ...(urlFilters.city && { city: urlFilters.city }),
    ...(urlFilters.type && { type: urlFilters.type }),
    ...(urlFilters.activityType && { activityType: urlFilters.activityType }),
  };

  const { data: benefitsListResponse, isSuccess } = useGetBenefits({
    page,
    pageSize: PAGE_SIZE,
    sortField,
    sortOrder,
    filters: apiFilters,
  });
  const { mutateAsync: deleteBenefit } = useDeleteBenefit();
  const { mutateAsync: deleteMultipleBenefits } = useDeleteMultipleBenefits();
  const { mutateAsync: duplicateBenefit } = useDuplicateBenefit();

  const navigateToBenefit = (benefitType: BenefitType, benefitId: string) => {
    navigate(
      BENEFIT_TO_PATH[benefitType].view.replace(':benefitId', benefitId),
      { state: { listSearchParams: urlFilters } },
    );
  };

  // Deleting a benefit used in a circuit or associated with another benefit
  // removes it from there: the red warning says so.
  const areBenefitsToDeleteUsedElsewhere =
    benefitsToDeleteIds.length > 0 && isSuccess
      ? benefitsListResponse.benefits
          .filter((benefit) => benefitsToDeleteIds.includes(benefit.id))
          .some(isBenefitUsedElsewhere)
      : false;

  const deleteSelectedBenefits = () => {
    if (benefitsToDeleteIds.length === 1)
      toastAsync(deleteBenefit({ benefitId: benefitsToDeleteIds[0] }), {
        success: t('trash.movedToTrash.single'),
      });
    else if (benefitsToDeleteIds.length > 0) {
      toastAsync(deleteMultipleBenefits({ benefitIds: benefitsToDeleteIds }), {
        success: t('trash.movedToTrash.multiple'),
      });
      setRowSelection({});
    }
  };

  return (
    <StyledContainer>
      <StyledListHeader
        title={t('benefits.list.title')}
        subTitle={t('benefits.list.subtitle')}
      >
        {selectedBenefits.length > 0 && (
          <StyledButton
            title={t('benefits.list.deleteSelected', {
              count: selectedBenefits.length,
            })}
            className="font-semibold gap-1 px-3 py-2"
            onClick={() => setBenefitsToDeleteIds(selectedBenefits)}
            icon={<TrashIcon className="w-5 h-5 stroke-brand-600" />}
            variant="outlined"
          />
        )}
        <StyledButton
          title={t('benefits.list.addBenefit')}
          className="font-semibold gap-1 px-3 py-2"
          onClick={() => setIsBenefitTypeModalOpen(true)}
          icon={<PlusIcon className="w-5 h-5 fill-white" />}
        />
      </StyledListHeader>
      <div className="flex flex-col w-full h-auto p-6 gap-6">
        <div className="flex flex-col gap-2">
          <span className="text-xs font-normal text-gray-600">
            {t('benefits.list.filterBy')}
          </span>
          <div className="flex flex-col md:flex-row justify-between gap-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex flex-col md:flex-row gap-4">
                <StyledSelect
                  className="min-w-1/4 md:min-w-48 lg:min-w-60"
                  control={control}
                  options={suppliersOptions ?? []}
                  name="supplierName"
                  placeholder={t('benefits.list.supplierPlaceholder')}
                  isClearable
                />
                <StyledSelect
                  className="min-w-1/4 md:min-w-48 lg:min-w-60"
                  control={control}
                  options={countriesOptions ?? COUNTRIES}
                  name="countryName"
                  placeholder={t('benefits.list.countryPlaceholder')}
                  isClearable
                />
                <StyledSelect
                  className="min-w-1/4 md:min-w-48 lg:min-w-60"
                  control={control}
                  options={citiesOptions ?? []}
                  name="cityName"
                  placeholder={t('benefits.list.cityPlaceholder')}
                  isClearable
                  isDisabled={!urlFilters.country}
                />
                <StyledSelect
                  className="min-w-1/4 md:min-w-48 lg:min-w-60"
                  control={control}
                  options={benefitTypeOptions ?? []}
                  name="benefitType"
                  placeholder={t('benefits.list.typePlaceholder')}
                  isClearable
                />
              </div>
            </div>
            <div className="w-full md:w-1/3 lg:w-1/4 2xl:w-1/5">
              <StyledInput
                control={control}
                name="search"
                placeholder={t('benefits.list.quickSearchPlaceholder')}
                icon={<SearchIcon className="w-5 h-5 stroke-gray-500" />}
              />
            </div>
          </div>
        </div>
        <StyledTable
          columns={getTranslatedConst(BENEFIT_COLUMNS)}
          data={benefitsListResponse?.benefits ?? []}
          sorting={sorting}
          setSorting={setSorting}
          getRowId={(row) => row.id}
          setSelection={setRowSelection}
          onDeleteRow={(row) => {
            setBenefitsToDeleteIds([row.original.id]);
          }}
          onDuplicateRow={(row) => {
            setBenefitToDuplicateId(row.original.id);
            setBenefitToDuplicateFields(
              getBenefitDuplicationFields({
                type: getBenefitTypeName(row.original.type),
                carRental: benefitsListResponse?.benefits.find(
                  (benefit) => benefit.id === row.original.id,
                )?.carRental,
                hotel: benefitsListResponse?.benefits.find(
                  (benefit) => benefit.id === row.original.id,
                )?.hotel,
                t,
              }),
            );
          }}
          onViewRow={(row) => {
            navigateToBenefit(
              getBenefitTypeName(row.original.type),
              row.original.id,
            );
          }}
          onRowClick={(row) => {
            navigateToBenefit(
              getBenefitTypeName(row.original.type),
              row.original.id,
            );
          }}
          onListRow={(row) => {
            setBenefitToDisplayCircuitsId(row.original.id);
          }}
          listEnablePredicate={(row) => isBenefitUsedElsewhere(row.original)}
        />
        {isSuccess && (
          <StyledNextPreviousButtons
            page={page}
            pageSize={PAGE_SIZE}
            setPage={setPage}
            dataLength={benefitsListResponse.total}
            label={t('benefits.list.benefit')}
          />
        )}
      </div>
      {isBenefitTypeModalOpen && (
        <ModalSelection
          selectAction={(option) => {
            navigate(BENEFIT_TO_PATH[option].create, {
              state: { listSearchParams: urlFilters },
            });
          }}
          actionTitle={t('benefits.list.next')}
          closeModal={() => setIsBenefitTypeModalOpen(false)}
          title={t('benefits.list.addBenefit')}
          message={t('benefits.addBenefitMessage')}
          options={BENEFIT_TYPES.toSorted((a, b) => a.localeCompare(b))}
          placeholder={t('benefits.list.selectBenefitPlaceholder')}
          optionValueToLabel={(value) =>
            getBenefitTypeTranslatedLabel(t, value)
          }
        />
      )}
      {benefitsToDeleteIds.length > 0 &&
        (areBenefitsToDeleteUsedElsewhere ? (
          <BenefitDeletionAlertModal
            closeModal={() => {
              setBenefitsToDeleteIds([]);
            }}
            actionOnClick={deleteSelectedBenefits}
            isMultiple={benefitsToDeleteIds.length > 1}
          />
        ) : (
          <ModalDeletion
            closeModal={() => {
              setBenefitsToDeleteIds([]);
            }}
            actionOnClick={deleteSelectedBenefits}
            type="prestation"
            isMultiple={benefitsToDeleteIds.length > 1}
            shouldCloseModalAfterAction={!areBenefitsToDeleteUsedElsewhere}
          />
        ))}
      {benefitToDuplicateId && (
        <ModalDuplication
          actionOnClick={async (selectedFields) => {
            if (benefitToDuplicateId) {
              const duplicatedBenefit = await duplicateBenefit({
                benefitId: benefitToDuplicateId,
                fields: selectedFields.flatMap((fields) => fields.split(';')),
              });
              navigateToBenefit(
                getBenefitTypeName(duplicatedBenefit.benefit.type),
                duplicatedBenefit.benefit.id,
              );
            }
          }}
          closeModal={() => {
            setBenefitToDuplicateId(undefined);
            setBenefitToDuplicateFields(undefined);
          }}
          fields={benefitToDuplicateFields ?? []}
          type="prestation"
          code={
            benefitsListResponse?.benefits.find(
              (benefit) => benefit.id === benefitToDuplicateId,
            )?.code ?? 0
          }
        />
      )}
      {benefitToDisplayCircuitsId && (
        <ListCircuitsUsingBenefitModal
          closeModal={() => {
            setBenefitToDisplayCircuitsId(null);
          }}
          benefitId={benefitToDisplayCircuitsId}
        />
      )}
    </StyledContainer>
  );
};

export default ListBenefitsPage;
